// UserProfilePage.tsx
import React, {useState, useEffect, useRef, useMemo} from 'react';
import {useParams, useNavigate, useLocation} from 'react-router-dom';
import {useAuth} from '../auth/AuthContext';
import api from '../api/axios';
import '../styles/UserProfilePage.css';
import { useClickable } from '../providers/ClickableProvider';
import ImageCropModal from '../components/ImageCropModal';
import {
    UserPlus, Users, Edit, Eye, BadgeCheck, Clock, XCircle, RefreshCw, Heart, HeartCrack, ListTodo,
    CalendarIcon, TrendingUp, Star, Bell, UserX, Activity, FileEdit, Trash, Check, X
} from 'lucide-react';

// Интерфейсы для данных
interface UserProfile {
    id: number;
    username: string;
    nickname: string;
    role: string;
    avatarUrl: string;
    bannerUrl: string;
    aboutMe: string;
}

interface UserStatistics {
    userId: number;
    totalAnimesWatched: number;
    totalEpisodesWatched: number;
    favoriteGenre: string;
}

interface UserAnimeStatus {
    id: number;
    animeId: number;
    animeTitle: string;
    animeImageUrl: string;
    status: string;
    rating: number;
    episodesWatched: number;
    isFavorite: boolean;
    animeEpisodes: number;
    averageRating: number;
    releaseYear?: number;
    genres?: string[];
}

interface FriendshipStatus {
    id: number;
    status: 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'BLOCKED';
    requester: {
        id: number;
        nickname: string;
        avatarUrl: string;
    };
    addressee: {
        id: number;
        nickname: string;
        avatarUrl: string;
    };
    createdAt: string;
    updatedAt: string;
}

interface Friend {
    id: string;
    name: string;
    avatar: string;
    bannerUrl?: string;
    aboutMe?: string;
}

interface UserActivityDTO {
    id: number;
    user: {
        id: number;
        username: string;
        nickname: string;
        avatarUrl: string;
    };
    anime: {
        id: number;
        title: string;
        imageUrl: string;
    };
    activityType: string;
    description: string;
    createdAt: string;
    oldRating?: number;
    newRating?: number;
    oldStatus?: string;
    newStatus?: string;
}

interface Subscription {
    id: string;
    name: string;
    avatar: string;
    subscribers: number;
}

interface ConnectionsState {
    friends: Friend[];
    followers: Friend[];
    following: Subscription[];
}

// Компонент StatsCard
const StatsCard = ({ title, count, icon, color, onClick, isClickable }: any) => {
    const getIconComponent = (iconName: string, color: string) => {
        const iconProps = { size: 38, color: color };

        switch (iconName) {
            case 'Eye':
                return <Eye {...iconProps} />;
            case 'BadgeCheck':
                return <BadgeCheck {...iconProps} />;
            case 'Clock':
                return <Clock {...iconProps} />;
            case 'XCircle':
                return <XCircle {...iconProps} />;
            case 'RefreshCw':
                return <RefreshCw {...iconProps} />;
            case 'Heart':
                return <Heart {...iconProps} />;
            case 'ListTodo':
                return <ListTodo {...iconProps} />;
            default:
                return <ListTodo {...iconProps} />;
        }
    };

    return (
        <div
            className="stats-card clickable"
            onClick={onClick}
            style={{cursor: 'pointer'}}
        >
            <div className="stats-card-content">
                <div className="stats-icon-wrapper">
                    {getIconComponent(icon, color)}
                </div>
                <div className="stats-text-content">
                    <p className="stats-card-title">{title}</p>
                    <p className="stats-card-count">{count}</p>
                </div>
            </div>
        </div>
    );
};

// Компонент ProfileHeader
const ProfileHeader = ({
                           username,
                           bio,
                           followers,
                           friends,
                           subscriptions,
                           isOwnProfile,
                           isEditing,
                           editData,
                           hasChanges,
                           onEditDataChange,
                           onAvatarChange,
                           onBannerChange,
                           onEditClick,
                           onSaveClick,
                           onCancelClick,
                           onAvatarDelete,
                           onBannerDelete,
                           avatarPreview,
                           bannerPreview,
                           currentAvatarUrl,
                           currentBannerUrl,
                           fileInputAvatarRef,
                           fileInputBannerRef,
                           friendshipStatus,
                           onFriendAction,
                           isAuthenticated,
                           userProfile,
                           currentUser,
                           isLoadingFriendship
                       }: any) => {

    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [pendingAction, setPendingAction] = useState<{
        type: string;
        friendshipId?: number;
        title: string;
    } | null>(null);

    const handleConfirmation = (confirmed: boolean, keepAsFollower?: boolean) => {
        if (confirmed && pendingAction) {
            // Выполняем действие
            switch (pendingAction.type) {
                case 'REMOVE':
                    onFriendAction('REMOVE', pendingAction.friendshipId, keepAsFollower);
                    break;
                case 'ACCEPT':
                    // ИЗМЕНЕНИЕ: если confirmed = true, то принимаем запрос
                    if (keepAsFollower === undefined) {
                        onFriendAction('ACCEPT', pendingAction.friendshipId);
                    }
                    break;
                case 'REJECT':
                    onFriendAction('REJECT', pendingAction.friendshipId);
                    break;
                case 'CANCEL':
                    onFriendAction('CANCEL', pendingAction.friendshipId);
                    break;
                default:
                    break;
            }
        } else if (!confirmed && pendingAction?.type === 'ACCEPT' && keepAsFollower === true) {
            // ИЗМЕНЕНИЕ: если rejected с keepAsFollower = true, то удаляем подписчика
            onFriendAction('REMOVE', pendingAction.friendshipId, false); // Удаляем полностью
        }
        // Если confirmed = false и keepAsFollower = false или undefined - ничего не делаем (отмена)

        setShowConfirmationModal(false);
        setPendingAction(null);
    };

    const showModal = (type: string, friendshipId?: number) => {
        let title = '';

        switch (type) {
            case 'REMOVE':
                title = 'Удалить из друзей?';
                break;
            case 'ACCEPT':
                // ИЗМЕНЕНИЕ: меняем заголовок для принятия запроса
                title = 'Запрос в друзья';
                break;
            case 'REJECT':
                title = 'Отклонить запрос?';
                break;
            case 'CANCEL':
                title = 'Отменить заявку?';
                break;
            default:
                return;
        }

        setPendingAction({
            type,
            friendshipId,
            title
        });
        setShowConfirmationModal(true);
    };

    const renderFriendButton = (status: any) => {
        const isRequester = status.requester.id === currentUser?.id;

        switch (status.status) {
            case 'PENDING':
                if (isRequester) {
                    // Если МЫ отправили запрос
                    return (
                        <button
                            className="friend-action-btn subscribed"
                            onClick={() => showModal('CANCEL', status.id)}
                            title="Отменить заявку в друзья"
                            disabled={isLoadingFriendship}
                        >
                            <Bell size={18} color="var(--text-button-purple)"/>
                            <span>Вы подписаны</span>
                        </button>
                    );
                } else {
                    // Если нам отправили запрос
                    return (
                        <button
                            className="friend-action-btn follower"
                            onClick={() => showModal('ACCEPT', status.id)}
                            title="Принять запрос в друзья"
                            disabled={isLoadingFriendship}
                        >
                            <UserPlus size={18} color="var(--text-button-blue)"/>
                            <span>Подписан на вас</span>
                        </button>
                    );
                }

            case 'ACCEPTED':
                return (
                    <button
                        className="friend-action-btn friend"
                        onClick={() => showModal('REMOVE', status.id)}
                        title="Удалить из друзей"
                        disabled={isLoadingFriendship}
                    >
                        <Users size={18} color="var(--text-button-green)"/>
                        <span>Друг</span>
                    </button>
                );

            case 'REJECTED':
                if (isRequester) {
                    // Если МЫ отправляли запрос, и он был отклонен
                    return (
                        <button
                            className="friend-action-btn rejected"
                            onClick={() => onFriendAction('RESEND')}
                            title="Запрос отклонен. Отправить снова"
                            disabled={isLoadingFriendship}
                        >
                            <UserX size={18} color="var(--text-button-red)"/>
                            <span>Запрос отклонен</span>
                        </button>
                    );
                } else {
                    // Если мы отклонили чужой запрос (но он остался подписанным)
                    return (
                        <button
                            className="friend-action-btn follower"
                            onClick={() => showModal('ACCEPT', status.id)}
                            title="Принять запрос в друзья"
                            disabled={isLoadingFriendship}
                        >
                            <UserPlus size={18} color="var(--text-button-blue)"/>
                            <span>Подписан на вас</span>
                        </button>
                    );
                }

            default:
                return (
                    <button
                        className="profile-friend-icon-btn"
                        onClick={() => onFriendAction('ADD')}
                        title="Добавить в друзья"
                        disabled={isLoadingFriendship}
                    >
                        {isLoadingFriendship ? (
                            <div className="loading-spinner-tiny"></div>
                        ) : (
                            <UserPlus size={18} color="white"/>
                        )}
                    </button>
                );
        }
    };

    const handleAvatarClick = () => {
        fileInputAvatarRef.current?.click();
    };

    const handleBannerClick = () => {
        fileInputBannerRef.current?.click();
    };

    return (
        <div className="profile-header-component">
            {/* Banner */}
            <div
                className="profile-banner-image"
                style={{
                    backgroundImage: `url('${isEditing && bannerPreview ? bannerPreview : (currentBannerUrl || '/user-images/default/banner.jpg')}')`
                }}
            >
                {isEditing && (
                    <div className="banner-edit-controls">
                        {/* Редактирование - слева */}
                        <button
                            className="edit-banner-btn"
                            onClick={handleBannerClick}
                            title="Изменить баннер"
                        >
                            <FileEdit size={16} color="white"/>
                        </button>
                        {/* Удаление - справа */}
                        {currentBannerUrl && currentBannerUrl !== '/user-images/default/banner.jpg' && (
                            <button
                                className="delete-banner-btn"
                                onClick={onBannerDelete}
                                title="Удалить баннер"
                            >
                                <Trash size={16} color="white"/>
                            </button>
                        )}
                        <input
                            type="file"
                            ref={fileInputBannerRef}
                            onChange={onBannerChange}
                            accept="image/jpeg,image/jpg,image/png,image/webp"
                            style={{display: 'none'}}
                        />
                    </div>
                )}
            </div>

            {/* Avatar and Info */}
            <div className="profile-header-content">
                <div className="profile-avatar-container-main">
                    <div className="profile-avatar-wrapper-main">
                        <div className="profile-avatar-image-container">
                            <img
                                src={isEditing && avatarPreview ? avatarPreview : (currentAvatarUrl || '/user-images/default/avatar.jpg')}
                                alt={username}
                                className="profile-avatar-img"
                            />
                            {isEditing && (
                                <div className="avatar-edit-controls">
                                    {/* Редактирование - слева */}
                                    <button
                                        className="edit-avatar-btn"
                                        onClick={handleAvatarClick}
                                        title="Изменить аватар"
                                    >
                                        <FileEdit size={16} color="white"/>
                                    </button>
                                    {/* Удаление - справа */}
                                    {currentAvatarUrl && currentAvatarUrl !== '/user-images/default/avatar.jpg' && (
                                        <button
                                            className="delete-avatar-btn"
                                            onClick={onAvatarDelete}
                                            title="Удалить аватар"
                                        >
                                            <Trash size={16} color="white"/>
                                        </button>
                                    )}
                                    <input
                                        type="file"
                                        ref={fileInputAvatarRef}
                                        onChange={onAvatarChange}
                                        accept="image/jpeg,image/jpg,image/png,image/webp"
                                        style={{display: 'none'}}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="profile-info-content">
                    <div className="profile-info-card">
                        <div className="profile-info-main">
                            <div className="profile-text-content">
                                {/* Никнейм - без редактирования */}
                                <h1 className="profile-display-name">{username}</h1>

                                {/* Описание с редактированием */}
                                {isEditing ? (
                                    <div className="edit-field">
                    <textarea
                        value={editData.aboutMe}
                        onChange={(e) => onEditDataChange('aboutMe', e.target.value)}
                        className="edit-textarea profile-description-edit"
                        placeholder="Расскажите о себе..."
                        maxLength={130}
                        rows={2}
                    />
                                        <div className="char-counter">
                                            {editData.aboutMe.length}/130
                                        </div>
                                    </div>
                                ) : (
                                    <p className="profile-description">{bio}</p>
                                )}

                                <div className="profile-stats-row">
                                    <div className="profile-stat-item">
                                        <UserPlus size={16} color="#cd69ff"/>
                                        <span className="stat-number">{followers}</span>
                                        <span className="stat-label">подписчиков</span>
                                    </div>
                                    <div className="profile-stat-item">
                                        <Users size={16} color="#ff69b4"/>
                                        <span className="stat-number">{friends}</span>
                                        <span className="stat-label">друзей</span>
                                    </div>
                                    <div className="profile-stat-item">
                                        <Bell size={16} color="#8a2be2"/>
                                        <span className="stat-number">{subscriptions}</span>
                                        <span className="stat-label">подписок</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons - теперь в правом верхнем углу карточки */}
                        <div className="profile-action-buttons-bottom-right">
                            {isOwnProfile ? (
                                <>
                                    {isEditing ? (
                                        <div className="edit-actions-icons">
                                            <button
                                                className="profile-cancel-icon-btn"
                                                onClick={onCancelClick}
                                                title="Отменить изменения"
                                            >
                                                <X size={18} color="#ef4444"/>
                                            </button>
                                            <button
                                                className="profile-save-icon-btn"
                                                onClick={onSaveClick}
                                                title="Сохранить изменения"
                                            >
                                                <Check size={18} color="#22c55e"/>
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            className="profile-edit-icon-btn"
                                            onClick={onEditClick}
                                            title="Редактировать"
                                        >
                                            <Edit size={18} color="white"/>
                                        </button>
                                    )}
                                </>
                            ) : (
                                <div className="friend-actions-container">
                                    {/* Показываем кнопки в зависимости от статуса дружбы */}
                                    {friendshipStatus ? (
                                        // Если есть статус дружбы - показываем соответствующую кнопку
                                        renderFriendButton(friendshipStatus)
                                    ) : (
                                        // Если статуса нет - показываем кнопку "Добавить в друзья"
                                        <button
                                            className="profile-friend-icon-btn"
                                            onClick={() => onFriendAction('ADD')}
                                            title="Добавить в друзья"
                                            disabled={isLoadingFriendship}
                                        >
                                            {isLoadingFriendship ? (
                                                <div className="loading-spinner-tiny"></div>
                                            ) : (
                                                <UserPlus size={18} color="white"/>
                                            )}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {showConfirmationModal && pendingAction && (
                <div className="confirmation-modal">
                    <div className="confirmation-modal-content">
                        <h3 className="confirmation-modal-title">{pendingAction.title}</h3>

                        {pendingAction.type === 'REMOVE' ? (
                            <>
                                <div className="confirmation-modal-actions-choice">
                                    <button
                                        className="confirm-modal-btn keep"
                                        onClick={() => handleConfirmation(true, true)}
                                        title="Человек останется подписан на вас"
                                    >
                                        <UserPlus size={16}/>
                                        <span>Удалить из друзей</span>
                                    </button>
                                    <button
                                        className="confirm-modal-btn remove"
                                        onClick={() => handleConfirmation(true, false)}
                                        title="Человек не будет подписан на вас"
                                    >
                                        <UserX size={16}/>
                                        <span>Удалить полностью</span>
                                    </button>
                                    <button
                                        className="confirm-modal-btn cancel"
                                        onClick={() => handleConfirmation(false)}
                                    >
                                        Отмена
                                    </button>
                                </div>
                            </>
                        ) : pendingAction.type === 'ACCEPT' ? (
                            // ИЗМЕНЕНИЕ: новая логика для окна "Запрос в друзья"
                            <div className="confirmation-modal-actions-choice">
                                <button
                                    className="confirm-modal-btn add-friend"
                                    onClick={() => handleConfirmation(true)} // Принимает запрос
                                    title="Добавить в друзья"
                                >
                                    <Users size={16} />
                                    <span>Добавить в друзья</span>
                                </button>
                                <button
                                    className="confirm-modal-btn remove-follower"
                                    onClick={() => handleConfirmation(false, true)} // Удаляет подписчика
                                    title="Удалить подписчика"
                                >
                                    <UserX size={16} />
                                    <span>Удалить подписчика</span>
                                </button>
                                <button
                                    className="confirm-modal-btn cancel"
                                    onClick={() => handleConfirmation(false, false)} // Оставляет как есть
                                >
                                    Отмена
                                </button>
                            </div>
                        ) : (
                            // Старая логика для остальных случаев
                            <div className="confirmation-modal-actions">
                                <button
                                    className="confirm-modal-btn no"
                                    onClick={() => handleConfirmation(false)}
                                >
                                    Нет
                                </button>
                                <button
                                    className="confirm-modal-btn yes"
                                    onClick={() => handleConfirmation(true)}
                                >
                                    Да
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Компонент AnimeStats
const AnimeStats = ({stats, onStatusClick, isOwnProfile, isAuthenticated, nickname}: any) => {
    const { makeClickable } = useClickable();
    const total = Object.values(stats).reduce((sum: number, val: any) => sum + val, 0);

    const statsData = [
        {title: 'Смотрю', count: stats.watching, icon: 'Eye', color: 'var(--text-button-blue)', status: 'WATCHING'},
        {title: 'Просмотрено', count: stats.completed, icon: 'BadgeCheck', color: 'var(--text-button-green)', status: 'COMPLETED'},
        {title: 'Запланировано', count: stats.planned, icon: 'Clock', color: 'var(--text-button-orange)', status: 'PLANNED'},
        {title: 'Брошено', count: stats.dropped, icon: 'XCircle', color: 'var(--text-button-red)', status: 'DROPPED'},
        {title: 'Пересматриваю', count: stats.rewatching, icon: 'RefreshCw', color: 'var(--text-button-purple)', status: 'REWATCHING'},
        {title: 'Любимое', count: stats.favorite, icon: 'Heart', color: '#ff57c2', status: 'FAVORITE'},
        {title: 'Все', count: stats.total, icon: 'ListTodo', color: 'var(--text-button-all)', status: 'ALL'},
    ];

    const handleCardClick = (status: string) => {
        // Разрешаем клик для:
        // 1. Авторизованных пользователей
        // 2. Неавторизованных пользователей, просматривающих чужой профиль (есть nickname)
        if (isAuthenticated || (!isAuthenticated && nickname && !isOwnProfile)) {
            if (onStatusClick) {
                onStatusClick(status);
            }
        }
    };

    // Определяем, кликабельны ли карточки
    const isClickable = isAuthenticated || (!isAuthenticated && nickname && !isOwnProfile);

    return (
        <div className="anime-stats-container animate-fade-in">
            <h2 className="stats-main-title">Статистика аниме</h2>
            <div className="stats-cards-grid">
                {statsData.map((stat, index) => {
                    const isCardClickable = isAuthenticated || (!isAuthenticated && nickname && !isOwnProfile);
                    const url = isCardClickable ?
                        (isOwnProfile ? `/my-list?status=${stat.status}` : `/users/${nickname}/list?status=${stat.status}`)
                        : '';

                    return (
                        <div
                            key={index}
                            {...(isCardClickable ? makeClickable(url, () => onStatusClick(stat.status)) : {})}
                        >
                            <StatsCard
                                title={stat.title}
                                count={stat.count}
                                icon={stat.icon}
                                color={stat.color}
                                isClickable={isCardClickable}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Компонент FavoriteAnimeList
const FavoriteAnimeList = ({animeList, isOwnProfile, nickname}: any) => {
    const {makeClickable} = useClickable();
    const navigate = useNavigate();
    const displayedAnime = animeList ? animeList.slice(0, 6) : [];

    const handleViewAll = () => {
        if (isOwnProfile) {
            // Для своего профиля - переход в свой список
            navigate('/my-list?status=FAVORITE');
        } else if (nickname) {
            // Для чужого профиля - переход в список этого пользователя
            navigate(`/users/${nickname}/list?status=FAVORITE`);
        }
    };

    // Функция для перехода на страницу аниме
    const handleAnimeClick = (animeId: number) => {
        navigate(`/anime/${animeId}`);
    };

    // Функция для форматирования жанров - показываем видимые и добавляем ... если есть скрытые
    const formatGenres = (genres: string | string[], maxVisible: number = 2) => {
        if (!genres) return '';

        // Если genres - строка, преобразуем в массив
        const genresArray = Array.isArray(genres)
            ? genres
            : typeof genres === 'string'
                ? genres.split(',').map(g => g.trim())
                : [];

        if (genresArray.length === 0) return '';

        // Берем видимые жанры
        const visibleGenres = genresArray.slice(0, maxVisible);
        const hasMore = genresArray.length > maxVisible;

        // Формируем строку с жанрами
        let result = visibleGenres.join(', ');

        // Добавляем ... если есть скрытые жанры
        if (hasMore) {
            result += '...';
        }

        return result;
    };

    // Функция для получения полного списка жанров (для tooltip)
    const getFullGenres = (genres: string | string[]) => {
        if (!genres) return 'Жанр не указан';

        const genresArray = Array.isArray(genres)
            ? genres
            : typeof genres === 'string'
                ? genres.split(',').map(g => g.trim())
                : [];

        return genresArray.length > 0 ? genresArray.join(', ') : 'Жанр не указан';
    };

    if (!displayedAnime || displayedAnime.length === 0) {
        return (
            <div className="favorite-anime-container animate-fade-in">
                <div className="favorite-header">
                    <Heart size={24} color="#ff57c2" fill="#ff57c2"/>
                    <h2 className="favorite-title">Любимое аниме</h2>
                </div>
                <div className="no-favorite-anime-message">
                    <p>В вашем списке нет любимых аниме</p>
                </div>
            </div>
        );
    }

    return (
        <div className="favorite-anime-container animate-fade-in">
            <div className="favorite-header">
                <Heart size={24} color="#ff57c2" fill="#ff57c2"/>
                <h2 className="favorite-title">Любимое аниме</h2>
                <button
                    className="view-all-favorites-btn"
                    {...makeClickable(isOwnProfile ? '/my-list?status=FAVORITE' : `/users/${nickname}/list?status=FAVORITE`, (e) => {
                        if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
                            handleViewAll();
                        }
                    })}
                    style={{cursor: 'pointer'}}
                >
                    Все
                </button>
            </div>

            <div className="anime-cards-grid">
                {displayedAnime.map((anime: any) => {
                    const formattedGenres = formatGenres(anime.genre);
                    const fullGenres = getFullGenres(anime.genre);

                    return (
                        <div
                            key={anime.id}
                            className="anime-card-item"
                            {...makeClickable(`/anime/${anime.animeId}`, (e) => {
                                if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
                                    handleAnimeClick(anime.animeId);
                                }
                            })}
                            style={{cursor: 'pointer'}}
                        >
                            <div className="anime-card-image-container">
                                <img
                                    src={anime.image}
                                    alt={anime.title}
                                    className="anime-card-image"
                                    onError={(e) => {
                                        e.currentTarget.src = '/images/default/poster.jpg';
                                    }}
                                />
                                <div className="anime-rating-badge">
                                    <Star size={14} color="#ffe033"/>
                                    <span className="rating-value-profile">{anime.rating}</span>
                                </div>
                            </div>

                            <div className="anime-card-content">
                                <h3 className="anime-card-title" title={anime.title}>
                                    {anime.title}
                                </h3>

                                <div className="anime-card-meta">
                                    <div className="meta-row">
                                        <div className="meta-item-profile">
                                            <span className="meta-text">{anime.year}</span>
                                            {/* Добавляем разделитель */}
                                            <span className="meta-divider"> / </span>
                                            {/* Показываем жанры с ... если обрезано */}
                                            <span
                                                className="genre-tag"
                                                title={fullGenres}
                                            >
                                                {formattedGenres || 'Неизвестно'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Компонент ActivityFeed
const ActivityFeed = ({activities, isOwnProfile, nickname}: any) => {
    const { makeClickable } = useClickable();
    const navigate = useNavigate();

    const getActivityIcon = (activityType: string, newStatus?: string) => {
        const iconProps = { size: 30 };

        // Для изменения статуса используем иконку целевого статуса
        if (activityType === 'STATUS_CHANGED' && newStatus) {
            return getStatusIcon(newStatus, 30);
        }

        // Остальные типы активности
        switch (activityType) {
            case 'RATING_ADDED':
            case 'RATING_CHANGED':
                return <Star {...iconProps} color="#ffe033" />;
            case 'EPISODES_WATCHED':
                return <Eye {...iconProps} color="#21e2ff" />;
            case 'ADDED_TO_FAVORITES':
                return <Heart {...iconProps} color="#ff57c2" />;
            case 'REMOVED_FROM_FAVORITES':
                return <HeartCrack {...iconProps} color="#ff57c2" />;
            default:
                return <ListTodo {...iconProps} color="#ffb6c1" />;
        }
    };

    const getActivityColor = (activityType: string, newStatus?: string) => {
        // Для изменения статуса используем цвет целевого статуса
        if (activityType === 'STATUS_CHANGED' && newStatus) {
            switch (newStatus) {
                case 'WATCHING':
                    return '#21e2ff';
                case 'COMPLETED':
                    return '#8fff3d';
                case 'PLANNED':
                    return '#ff8c0e';
                case 'DROPPED':
                    return '#ff0300';
                case 'REWATCHING':
                    return '#cd69ff';
                default:
                    return '#ffb6c1';
            }
        }

        // Остальные типы активности
        switch (activityType) {
            case 'RATING_ADDED':
            case 'RATING_CHANGED':
                return '#ffe033';
            case 'EPISODES_WATCHED':
                return '#21e2ff';
            case 'ADDED_TO_FAVORITES':
            case 'REMOVED_FROM_FAVORITES':
                return '#ff57c2';
            default:
                return 'hsl(260, 70%, 55%)';
        }
    };

    const getStatusIcon = (status: string, size: number = 30) => {
        const iconProps = { size };

        switch (status) {
            case 'WATCHING':
                return <Eye {...iconProps} color="#21e2ff" />;
            case 'COMPLETED':
                return <BadgeCheck {...iconProps} color="#8fff3d" />;
            case 'PLANNED':
                return <Clock {...iconProps} color="#ff8c0e" />;
            case 'DROPPED':
                return <XCircle {...iconProps} color="#ff0300" />;
            case 'REWATCHING':
                return <RefreshCw {...iconProps} color="#cd69ff" />;
            default:
                return <ListTodo {...iconProps} color="#ffb6c1" />;
        }
    };

    const formatActivityText = (activity: any) => {
        const { description } = activity;

        // Просто возвращаем description, так как он уже содержит правильный текст
        return description || 'Выполнил действие';
    };

    const getStatusDisplayName = (status: string) => {
        const statusMap: { [key: string]: string } = {
            'WATCHING': 'Смотрю',
            'COMPLETED': 'Просмотрено',
            'PLANNED': 'Запланировано',
            'DROPPED': 'Брошено',
            'REWATCHING': 'Пересматриваю'
        };
        return statusMap[status] || status;
    };

    const formatTimestamp = (timestamp: string) => {

        const date = new Date(timestamp + 'Z');

        const now = new Date();

        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'только что';
        if (diffMins < 60) return `${diffMins} мин назад`;
        if (diffHours < 24) return `${diffHours} ч назад`;
        if (diffDays < 7) return `${diffDays} д назад`;

        return date.toLocaleDateString('ru-RU', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const handleAnimeClick = (animeId: number) => {
        navigate(`/anime/${animeId}`);
    };

    const handleViewAllActivities = () => {
        if (isOwnProfile) {
            // Для своего профиля - переход на страницу полной активности
            navigate('/activity');
        } else if (nickname) {
            // Для чужого профиля - переход на страницу активности этого пользователя
            navigate(`/users/${nickname}/activity`);
        }
    };

    if (!activities || activities.length === 0) {
        return (
            <div className="activity-feed-container animate-fade-in">
                <div className="activity-header">
                    <h3 className="activity-feed-title">
                        <Activity size={24} color="#cd69ff" />
                        Недавняя активность
                    </h3>
                </div>
                <div className="activity-scroll-area">
                    <div className="no-activities-message">
                        <p>Активности пока нет</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="activity-feed-container animate-fade-in">
            <div className="activity-header">
                <h3 className="activity-feed-title">
                    <Activity size={24} color="#cd69ff" />
                    Недавняя активность
                </h3>
            </div>

            {/* Добавляем контейнер со скроллом */}
            <div className="activity-scroll-area">
                <div className="activity-list">
                    {activities.map((activity: any) => (
                        <div
                            key={activity.id}
                            className="activity-item-card"
                            {...makeClickable(`/anime/${activity.anime.id}`, (e) => {
                                if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
                                    handleAnimeClick(activity.anime.id);
                                }
                            })}
                            style={{cursor: 'pointer'}}
                        >
                            <div
                                className="activity-type-icon"
                                style={{
                                    backgroundColor: `${getActivityColor(activity.activityType, activity.newStatus)}20`
                                }}
                            >
                                {getActivityIcon(activity.activityType, activity.newStatus)}
                            </div>

                            <div className="activity-content">
                                <p className="activity-anime-name">{activity.anime.title}</p>
                                <div className="activity-description-row">
                                    <p className="activity-description">
                                        {formatActivityText(activity)}
                                    </p>
                                    <p className="activity-timestamp">
                                        {formatTimestamp(activity.createdAt)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

interface ConnectionsBlockProps {
    friends: Friend[];
    followers: Friend[];
    subscriptions: Subscription[];
    currentUserNickname?: string;
}

const ConnectionsBlock = ({
                              friends,
                              followers,
                              subscriptions,
                              currentUserNickname // Добавляем этот пропс
                          }: ConnectionsBlockProps) => {
    const { makeClickable } = useClickable();
    const [activeTab, setActiveTab] = useState<'friends' | 'followers' | 'following'>('friends');
    const navigate = useNavigate();

    const handleConnectionClick = (connectionId: string, name: string) => {
        // Если нажали на себя или name не указан, не делаем ничего
        if (!name || name === currentUserNickname) return;
        navigate(`/users/${name}`);
    };

    const getActiveData = () => {
        switch (activeTab) {
            case 'friends':
                return {
                    data: friends,
                    title: 'Друзья',
                    icon: <Users size={24} color="#ff69b4" />,
                    count: friends.length,
                    emptyText: 'Пока нет друзей',
                    viewAllRoute: '/friends'
                };
            case 'followers':
                return {
                    data: followers,
                    title: 'Подписчики',
                    icon: <UserPlus size={24} color="#cd69ff" />,
                    count: followers.length,
                    emptyText: 'Пока нет подписчиков',
                    viewAllRoute: '/followers'
                };
            case 'following':
                return {
                    data: subscriptions,
                    title: 'Подписки',
                    icon: <Bell size={24} color="#8a2be2" />,
                    count: subscriptions.length,
                    emptyText: 'Пока нет подписок',
                    viewAllRoute: '/following'
                };
        }
    };

    const activeData = getActiveData();

    // Ограничиваем отображение до 8 элементов
    const displayedData = activeData.data.slice(0, 8);

    const handleViewAll = () => {
        if (currentUserNickname) {
            // Для чужого профиля
            navigate(`/users/${currentUserNickname}/${activeTab}?tab=${activeTab}`);
        } else {
            // Для своего профиля
            navigate(`/${activeTab}?tab=${activeTab}`);
        }
    };

    return (
        <div className="connections-container animate-fade-in">
            {/* Заголовок с табами и кнопкой "Все" */}
            <div className="connections-header-with-all">
                <div className="connections-header-top">
                    <h3 className="connections-title">
                        {activeData.icon}
                        {activeData.title}
                    </h3>

                    {activeData.data.length > 0 && (
                        <button
                            className="view-all-connections-btn-header"
                            {...makeClickable(currentUserNickname
                                ? `/users/${currentUserNickname}/${activeTab}?tab=${activeTab}`
                                : `/${activeTab}?tab=${activeTab}`, (e) => {
                                if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
                                    handleViewAll();
                                }
                            })}
                            style={{cursor: 'pointer'}}
                        >
                            Все
                        </button>
                    )}
                </div>

                {/* Табы для переключения - количество на самих кнопках */}
                <div className="connections-tabs">
                    <button
                        className={`connection-tab ${activeTab === 'friends' ? 'active' : ''}`}
                        onClick={() => setActiveTab('friends')}
                    >
                        <Users size={16} />
                        <span>Друзья ({friends.length})</span>
                    </button>
                    <button
                        className={`connection-tab ${activeTab === 'followers' ? 'active' : ''}`}
                        onClick={() => setActiveTab('followers')}
                    >
                        <UserPlus size={16} />
                        <span>Подписчики ({followers.length})</span>
                    </button>
                    <button
                        className={`connection-tab ${activeTab === 'following' ? 'active' : ''}`}
                        onClick={() => setActiveTab('following')}
                    >
                        <Bell size={16} />
                        <span>Подписки ({subscriptions.length})</span>
                    </button>
                </div>
            </div>

            {/* Содержимое активного таба */}
            <div className="connections-content">
                {displayedData.length > 0 ? (
                    <div className="connections-grid-4x2">
                        {displayedData.map((item: any) => (
                            <div
                                key={item.id}
                                className="connection-card-item"
                                {...makeClickable(`/users/${item.name}`, (e) => {
                                    if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
                                        handleConnectionClick(item.id, item.name);
                                    }
                                })}
                                style={{cursor: 'pointer'}}
                            >
                                <div className="connection-avatar-container">
                                    <div className="connection-avatar-image">
                                        <img
                                            src={item.avatar}
                                            alt={item.name}
                                            onError={(e) => {
                                                e.currentTarget.src = '/user-images/default/avatar.jpg';
                                            }}
                                        />
                                    </div>
                                    {/* Добавляем класс для ограничения текста */}
                                    <p className="connection-name limited-text" title={item.name}>
                                        {item.name}
                                    </p>
                                    {/* УБРАЛИ всю дополнительную информацию */}
                                </div>
                                {/* УБРАЛИ кнопки действий */}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="connections-empty-state">
                        <p className="connections-empty-text">{activeData.emptyText}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

const DistributionChart = ({stats}: any) => {
    const totalForChart = (stats.watching || 0) +
        (stats.completed || 0) +
        (stats.planned || 0) +
        (stats.dropped || 0) +
        (stats.rewatching || 0);

    const chartData = [
        {
            label: 'Смотрю',
            color: '#21e2ff',
            value: stats.watching,
            percentage: totalForChart > 0 ? (stats.watching / totalForChart) * 100 : 0
        },
        {
            label: 'Просмотрено',
            color: '#8fff3d',
            value: stats.completed,
            percentage: totalForChart > 0 ? (stats.completed / totalForChart) * 100 : 0},
        {label: 'Запланировано', color: '#ff8c0e', value: stats.planned, percentage: totalForChart > 0 ? (stats.planned / totalForChart) * 100 : 0},
        {label: 'Брошено', color: '#ff0300', value: stats.dropped, percentage: totalForChart > 0 ? (stats.dropped / totalForChart) * 100 : 0},
        {label: 'Пересматриваю', color: '#cd69ff', value: stats.rewatching, percentage: totalForChart > 0 ? (stats.rewatching / totalForChart) * 100 : 0},
    ];

    let currentAngle = 0;
    const radius = 80;
    const strokeWidth = 0.1;

    // Проверяем, есть ли хоть одно ненулевое значение
    const hasData = totalForChart > 0;

    // ФИКС: проверяем, есть ли хотя бы одно ненулевое значение в данных
    const hasNonZeroData = chartData.some(item => item.value > 0);

    return (
        <div className="distribution-chart-container animate-fade-in">
            <h3 className="chart-title-left">Распределение списков</h3>
            <div className="chart-content">
                {hasData && hasNonZeroData ? ( // ФИКС: используем обе проверки
                    <>
                        <div className="pie-chart-wrapper">
                            <svg width="180" height="180" viewBox="0 0 180 180">
                                {chartData.map((item, index) => {
                                    // Если значение 0 - пропускаем сегмент
                                    if (item.value === 0) return null;

                                    const angle = (item.percentage / 100) * 360;
                                    const largeArcFlag = angle > 180 ? 1 : 0;

                                    // ФИКС: для случая с одним значением (100%) рисуем полный круг
                                    if (item.percentage === 100) {
                                        return (
                                            <circle
                                                key={index}
                                                cx="90"
                                                cy="90"
                                                r={radius}
                                                fill={item.color}
                                                stroke="black"
                                                strokeWidth={strokeWidth}
                                            />
                                        );
                                    }

                                    const x1 = 90 + radius * Math.cos(currentAngle * Math.PI / 180);
                                    const y1 = 90 + radius * Math.sin(currentAngle * Math.PI / 180);

                                    const x2 = 90 + radius * Math.cos((currentAngle + angle) * Math.PI / 180);
                                    const y2 = 90 + radius * Math.sin((currentAngle + angle) * Math.PI / 180);

                                    const pathData = [
                                        `M 90 90`,
                                        `L ${x1} ${y1}`,
                                        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                                        `Z`
                                    ].join(' ');

                                    currentAngle += angle;

                                    return (
                                        <path
                                            key={index}
                                            d={pathData}
                                            fill={item.color}
                                            stroke="black"
                                            strokeWidth={strokeWidth}
                                        />
                                    );
                                })}
                            </svg>
                        </div>

                        {/* Легенда внизу - показываем только ненулевые значения */}
                        <div className="chart-legend-flex">
                            {chartData
                                .filter(item => item.value > 0)
                                .map((item, index) => (
                                    <div key={index} className="legend-item">
                                        <div
                                            className="legend-color"
                                            style={{backgroundColor: item.color}}
                                        />
                                        <span
                                            className="legend-label"
                                            style={{color: item.color}}
                                        >
                                            {item.label} ({item.value})
                                        </span>
                                    </div>
                                ))
                            }
                        </div>
                    </>
                ) : (
                    // Сообщение когда данных нет
                    <div className="no-data-message">
                        Для отображения добавьте аниме в список
                    </div>
                )}
            </div>
        </div>
    );
};

// Компонент ProgressChart - прогресс завершения
const ProgressChart = ({ stats }: any) => {
    const completed = stats.completed || 0;
    const total = (stats.watching || 0) +
        (stats.completed || 0) +
        (stats.planned || 0) +
        (stats.dropped || 0) +
        (stats.rewatching || 0);
    const progress = total > 0 ? Math.round((completed / total) * 100) : 0;

    const radius = 80;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference * (1 - progress / 100);

    // Проверяем, есть ли данные
    const hasData = total > 0;

    return (
        <div className="progress-chart-container animate-fade-in">
            <h3 className="chart-title-left">
                Прогресс завершения
            </h3>

            <div className="progress-content">
                {hasData ? (
                    <>
                        <div className="circular-progress-wrapper">
                            <div className="circular-progress-container">
                                <svg
                                    className="circular-progress-svg"
                                    viewBox="0 0 180 180"
                                >
                                    {/* Фоновое кольцо */}
                                    <circle
                                        className="progress-ring-bg"
                                        cx="90"
                                        cy="90"
                                        r={radius}
                                    />
                                    {/* Прогресс */}
                                    <circle
                                        className="progress-ring-fill"
                                        cx="90"
                                        cy="90"
                                        r={radius}
                                        style={{
                                            strokeDasharray: circumference,
                                            strokeDashoffset: strokeDashoffset
                                        }}
                                    />
                                </svg>

                                {/* Внутренний круг с процентами */}
                                <div className="progress-inner-simple">
                                    <span className="progress-percentage">
                                        {progress}%
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Линейный прогресс внизу */}
                        <div className="linear-progress-bottom">
                            <div className="progress-labels">
                                <span className="progress-label">Завершено</span>
                                <span className="progress-count">
                                    {completed} из {total}
                                </span>
                            </div>
                            <div className="linear-progress-bar">
                                <div
                                    className="linear-progress-fill"
                                    style={{width: `${progress}%`}}
                                />
                            </div>
                        </div>
                    </>
                ) : (
                    // Сообщение когда данных нет
                    <div className="no-data-message">
                        Для отображения добавьте аниме в список
                    </div>
                )}
            </div>
        </div>
    );
};

// Компонент WatchingActivityChart - финальная версия
const WatchingActivityChart = ({ isOwnProfile, nickname }: { isOwnProfile: boolean; nickname?: string }) => {
    const { isAuthenticated } = useAuth();
    const [period, setPeriod] = useState<string>('month');
    const [selectedMonth, setSelectedMonth] = useState<string>(
        new Date().toISOString().slice(0, 7)
    );
    const [selectedYear, setSelectedYear] = useState<string>(
        new Date().getFullYear().toString()
    );
    const [showPeriodModal, setShowPeriodModal] = useState(false);
    const [statistics, setStatistics] = useState<any>(null);
    const [totalStatistics, setTotalStatistics] = useState<any>(null);
    const [loading, setLoading] = useState(false);

    // Добавляем функцию для получения общей статистики
    const fetchTotalStatistics = async () => {
        if (!isOwnProfile && nickname) {
            // Для чужого профиля получаем общую статистику из публичного endpoint'а
            try {
                const response = await api.get(`/users/${nickname}/statistics`);
                if (response && response.data) {
                    setTotalStatistics({
                        totalAnimesWatched: response.data.totalAnimesWatched || 0,
                        totalEpisodesWatched: response.data.totalEpisodesWatched || 0
                    });
                }
            } catch (error) {
                console.error('Error fetching total statistics for other user:', error);
            }
            return;
        }

        // Для своего профиля
        if (!isAuthenticated || !isOwnProfile) return;

        try {
            const response = await api.get(`/user/statistics`);
            if (response && response.data) {
                setTotalStatistics(response.data);
            }
        } catch (error) {
            console.error('Error fetching total statistics:', error);
        }
    };

    // Генерация годов для выбора
    const getYearOptions = () => {
        const years = [];
        for (let i = 2025; i <= 2045; i++) {
            years.push(i);
        }
        return years;
    };

    // Получение отображаемого названия периода
    const getPeriodDisplayName = () => {
        switch (period) {
            case 'month':
                return `Месяц: ${new Date(selectedMonth + '-01').toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })}`;
            case 'year':
                return `Год: ${selectedYear}`;
            default:
                return 'Выбрать период';
        }
    };

    // Получение статистики
    const fetchStatistics = async (periodType: string) => {
        console.log('fetchStatistics called:', {
            periodType,
            isAuthenticated,
            isOwnProfile,
            nickname,
            selectedMonth,
            selectedYear
        });

        // ВАЖНО: Разделяем логику для своего и чужого профиля
        if (!isOwnProfile && nickname) {
            // ЧУЖОЙ ПРОФИЛЬ - используем публичные endpoints
            console.log('Fetching for OTHER user profile:', nickname);
            setLoading(true);
            try {
                let response;

                switch (periodType) {
                    case 'month':
                        const [year, month] = selectedMonth.split('-');
                        response = await api.get(`/users/${nickname}/statistics/watch/month/${year}/${month}`);
                        break;
                    case 'year':
                        const selectedYearNum = parseInt(selectedYear);
                        response = await api.get(`/users/${nickname}/statistics/watch/year/${selectedYearNum}`);
                        break;
                    default:
                        console.log('Unsupported period type:', periodType);
                        return;
                }

                if (response && response.data) {
                    console.log('API response for other user:', response.data);
                    setStatistics(response.data);
                } else {
                    console.log('No data from API for other user');
                    setStatistics(null); // Устанавливаем null вместо тестовых данных
                }
            } catch (error) {
                console.error('Error fetching statistics for other user:', error);
                setStatistics(null); // Устанавливаем null вместо тестовых данных
            } finally {
                setLoading(false);
            }
            return;
        }

        // СВОЙ ПРОФИЛЬ - используем приватные endpoints
        if (isAuthenticated && isOwnProfile) {
            console.log('Fetching for OWN profile');
            setLoading(true);
            try {
                let response;

                switch (periodType) {
                    case 'month':
                        const [year, month] = selectedMonth.split('-');
                        response = await api.get(`/user/statistics/watch/month/${year}/${month}`);
                        break;
                    case 'year':
                        const selectedYearNum = parseInt(selectedYear);
                        response = await api.get(`/user/statistics/watch/year/${selectedYearNum}`);
                        break;
                    default:
                        console.log('Unsupported period type:', periodType);
                        return;
                }

                if (response && response.data) {
                    console.log('API response for own profile:', response.data);
                    setStatistics(response.data);
                } else {
                    console.log('No data from API for own profile');
                    setStatistics(null); // Устанавливаем null вместо тестовых данных
                }
            } catch (error) {
                console.error('Error fetching statistics for own profile:', error);
                setStatistics(null); // Устанавливаем null вместо тестовых данных
            } finally {
                setLoading(false);
            }
            return;
        }

        // Если не подошли ни под один случай
        console.log('No valid case for fetching statistics');
        setStatistics(null); // Устанавливаем null вместо тестовых данных
    };

    // Применение выбранного периода
    const handleApplyPeriod = () => {
        if ((isAuthenticated && isOwnProfile) || nickname) {
            fetchStatistics(period);
        }
        setShowPeriodModal(false);
    };

    // Загрузка статистики при монтировании
    useEffect(() => {
        const loadInitialData = async () => {
            console.log('Loading initial data:', { isAuthenticated, isOwnProfile, nickname });

            await fetchTotalStatistics();

            if (!isOwnProfile && nickname) {
                console.log('Loading initial chart data for OTHER user');
                await fetchStatistics('month');
            } else if (isAuthenticated && isOwnProfile) {
                console.log('Loading initial chart data for OWN user');
                await fetchStatistics('month');
            } else {
                console.log('No data available');
                setStatistics(null); // Устанавливаем null вместо тестовых данных
            }
        };

        loadInitialData();
    }, [isAuthenticated, isOwnProfile, nickname]);

    // Генерация данных для графика
    const getChartData = () => {
        if (!statistics || !statistics.statistics) return [];

        console.log('Raw API data:', statistics);

        return statistics.statistics.map((stat: any) => ({
            date: stat.date,
            animes: stat.animesWatched || 0,
            episodes: stat.episodesWatched || 0,
            hasData: (stat.animesWatched || 0) > 0 || (stat.episodesWatched || 0) > 0
        }));
    };

    // Форматирование даты для отображения
    const formatDate = (dateString: string, periodType: string) => {
        const date = new Date(dateString);

        switch (periodType) {
            case 'day':
                return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
            case 'month':
                return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
            case 'year':
                return date.toLocaleDateString('ru-RU', { month: 'short' });
            default:
                return date.toLocaleDateString('ru-RU');
        }
    };

    // ФИКС: Получение максимального значения с запасом 15%
    const getMaxValue = () => {
        const data = getChartData();
        if (data.length === 0) return 10;

        const maxEpisodes = Math.max(...data.map((d: any) => d.episodes));
        // Добавляем 15% запаса сверху для лучшего отображения
        const maxVal = maxEpisodes * 1.15;
        return Math.ceil(maxVal / 5) * 5 || 10;
    };

    // Получение значений для вертикальной оси
    const getYAxisValues = () => {
        const maxValue = getMaxValue();
        const step = maxValue / 4;

        return [
            Math.round(step * 4),
            Math.round(step * 3),
            Math.round(step * 2),
            Math.round(step),
            0
        ];
    };

    // ФИКС: Расчет позиции точек от края до края
    const getPointPosition = (index: number, totalPoints: number) => {
        if (totalPoints <= 1) return 50;

        // Точки от 0% до 100% - от левого края до правого края
        return (index / (totalPoints - 1)) * 100;
    };

    // ФИКС: Получение индексов для отображения меток на оси X
    const getVisibleTickIndexes = () => {
        if (!statistics) return [];

        const dataLength = chartData.length;
        const periodType = statistics.period || 'day';

        // Для месяца
        if (periodType === 'month') {
            if (dataLength <= 6) {
                return Array.from({ length: dataLength }, (_, i) => i);
            }

            const indexes: number[] = [];
            const step = (dataLength - 1) / 5; // 6 меток = 5 промежутков

            for (let i = 0; i < 6; i++) {
                const index = Math.min(Math.round(i * step), dataLength - 1);
                indexes.push(index);
            }

            return Array.from(new Set(indexes)).sort((a, b) => a - b);
        }

        // Для года
        if (periodType === 'year') {
            return Array.from({ length: Math.min(12, dataLength) }, (_, i) => i);
        }

        // Для других периодов (по умолчанию 6 меток)
        if (dataLength <= 6) {
            return Array.from({ length: dataLength }, (_, i) => i);
        }

        const indexes: number[] = [0];
        const step = Math.ceil((dataLength - 1) / 5); // 6 меток = 5 промежутков
        for (let i = step; i < dataLength - 1; i += step) {
            indexes.push(i);
        }
        indexes.push(dataLength - 1);
        return indexes;
    };

    const chartData = getChartData();
    const yAxisValues = getYAxisValues();
    const maxValue = getMaxValue();
    const visibleTickIndexes = getVisibleTickIndexes();

    if (!isAuthenticated && !nickname) {
        return (
            <div className="activity-chart-container animate-fade-in">
                <div className="chart-header">
                    <div>
                        <h3 className="chart-main-title">
                            <TrendingUp size={24} color="#cd69ff"/>
                            График активности просмотров
                        </h3>
                    </div>
                </div>
                <div className="chart-placeholder-container">
                    <div className="chart-placeholder-content">
                        <p className="placeholder-text">
                            Войдите в систему для просмотра статистики
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="activity-chart-container animate-fade-in">
            <div className="chart-header">
                <div>
                    <h3 className="chart-main-title">
                        <TrendingUp size={24} color="#cd69ff"/>
                        График активности просмотров
                    </h3>
                </div>

                {(isAuthenticated && isOwnProfile) || nickname ? (
                    <button
                        className="period-selector-btn"
                        onClick={() => setShowPeriodModal(true)}
                    >
                        <CalendarIcon size={16} color="var(--text-primary)"/>
                        {getPeriodDisplayName()}
                    </button>
                ) : null}
            </div>

            {/* Stats Summary */}
            <div className="chart-stats-container">
                {/* Первая линия - заголовки */}
                <div className="stats-titles-row">
                    <div className="stats-period-title">
                        За выбранный период
                    </div>
                    <div className="stats-period-title total-stats-title">
                        За все время
                    </div>
                </div>

                {/* Вторая линия - блоки "Просмотрено аниме" */}
                <div className="stats-items-row">
                    <div className="chart-stat-item">
                        <div className="chart-stat-label">Просмотрено аниме</div>
                        <div className="chart-stat-value">
                            {statistics?.totalAnimesWatched || 0}
                        </div>
                    </div>
                    <div className="chart-stat-item">
                        <div className="chart-stat-label">Просмотрено аниме</div>
                        <div className="chart-stat-value">
                            {totalStatistics?.totalAnimesWatched || 0}
                        </div>
                    </div>
                </div>

                {/* Третья линия - блоки "Просмотрено серий" */}
                <div className="stats-items-row">
                    <div className="chart-stat-item">
                        <div className="chart-stat-label">Просмотрено серий</div>
                        <div className="chart-stat-value">
                            {statistics?.totalEpisodesWatched || 0}
                        </div>
                    </div>
                    <div className="chart-stat-item">
                        <div className="chart-stat-label">Просмотрено серий</div>
                        <div className="chart-stat-value">
                            {totalStatistics?.totalEpisodesWatched || 0}
                        </div>
                    </div>
                </div>
            </div>

            {/* Chart */}
            <div className="chart-content-wrapper">
                {loading ? (
                    <div className="chart-loading">
                        <div className="loading-spinner-small"></div>
                        <p>Загрузка статистики...</p>
                    </div>
                ) : chartData.length > 0 ? (
                    <div className="chart-with-axes">
                        {/* ФИКС: Вертикальная ось с отступом */}
                        <div className="y-axis" style={{bottom: '40px'}}>
                            {yAxisValues.map((value) => (
                                <div key={value} className="y-tick">
                                    <span className="y-tick-label">{value}</span>
                                </div>
                            ))}
                        </div>

                        {/* Основная область графика */}
                        <div className="chart-main-area">
                            {/* Область с линиями */}
                            <div className="chart-area">
                                {/* Сетка */}
                                <div className="chart-grid">
                                    {yAxisValues.map((value, index) => (
                                        <div
                                            key={index}
                                            className="grid-line"
                                            style={{
                                                bottom: `${(value / maxValue) * 100}%`
                                            }}
                                        />
                                    ))}
                                </div>

                                {/* SVG для линии серий */}
                                <svg
                                    className="chart-lines-svg"
                                    viewBox="0 0 100 100"
                                    preserveAspectRatio="none"
                                >
                                    {/* ФИКС: Линия от края до края */}
                                    <path
                                        className="episodes-line"
                                        d={chartData.map((point: any, index: number) => {
                                            const x = getPointPosition(index, chartData.length);
                                            const y = 100 - (point.episodes / maxValue) * 100;
                                            return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
                                        }).join(' ')}
                                    />
                                </svg>

                                {/* ФИКС: Точки данных от края до края */}
                                <div className="chart-points">
                                    {chartData.map((point: any, index: number) => (
                                        <div
                                            key={index}
                                            className="data-point-container"
                                            style={{
                                                left: `${getPointPosition(index, chartData.length)}%`
                                            }}
                                        >
                                            <div
                                                className={`data-point episodes-point ${point.episodes === 0 ? 'zero-point' : ''}`}
                                                style={{
                                                    bottom: `${(point.episodes / maxValue) * 100}%`,
                                                    opacity: point.episodes === 0 ? 0.3 : 1
                                                }}
                                                title={`${formatDate(point.date, statistics?.period || 'day')}\nАниме: ${point.animes}\nСерии: ${point.episodes}`}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* ФИКС: Горизонтальная ось с увеличенной высотой */}
                            <div className="x-axis" style={{height: '40px'}}>
                                {statistics && visibleTickIndexes.map((dataIndex) => {
                                    const point = chartData[dataIndex];
                                    if (!point) return null;

                                    const position = getPointPosition(dataIndex, chartData.length);

                                    return (
                                        <div
                                            key={dataIndex}
                                            className="x-tick"
                                            style={{
                                                position: 'absolute',
                                                left: `${position}%`,
                                                transform: 'translateX(-50%)',
                                                bottom: '10px'
                                            }}
                                        >
                                            <span className="x-tick-label">
                                                {formatDate(point.date, statistics.period)}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="chart-placeholder-container">
                        <div className="chart-placeholder-content">
                            <p className="placeholder-text">
                                Нет данных за выбранный период
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Модальное окно выбора периода */}
            {showPeriodModal && (
                <div className="modal-overlay">
                    <div className="modal-content period-modal">
                        <h3>Выбор периода</h3>

                        <div className="period-options">
                            <label className="period-option">
                                <input
                                    type="radio"
                                    name="period"
                                    value="month"
                                    checked={period === 'month'}
                                    onChange={(e) => setPeriod(e.target.value)}
                                />
                                <span>Месяц</span>
                            </label>

                            <label className="period-option">
                                <input
                                    type="radio"
                                    name="period"
                                    value="year"
                                    checked={period === 'year'}
                                    onChange={(e) => setPeriod(e.target.value)}
                                />
                                <span>Год</span>
                            </label>
                        </div>

                        {/* Выбор месяца */}
                        {period === 'month' && (
                            <div className="period-input">
                                <label>Месяц:</label>
                                <input
                                    type="month"
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(e.target.value)}
                                    min="2025-01"
                                    max="2045-12"
                                />
                            </div>
                        )}

                        {/* Выбор года */}
                        {period === 'year' && (
                            <div className="period-input">
                                <label>Год:</label>
                                <select
                                    value={selectedYear}
                                    onChange={(e) => setSelectedYear(e.target.value)}
                                    size={5}
                                >
                                    {getYearOptions().map(year => (
                                        <option key={year} value={year}>
                                            {year}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className="modal-actions-watch">
                            <button
                                className="modal-cancel-btn"
                                onClick={() => setShowPeriodModal(false)}
                            >
                                Отмена
                            </button>
                            <button
                                className="modal-apply-btn"
                                onClick={handleApplyPeriod}
                                disabled={!((isAuthenticated && isOwnProfile) || nickname)}
                            >
                                Применить
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};


// Основной компонент страницы
const UserProfilePage: React.FC = () => {
    const {nickname} = useParams<{ nickname: string }>();
    const location = useLocation();
    const {isAuthenticated, user: currentUser} = useAuth();
    const navigate = useNavigate();
    const fileInputAvatarRef = useRef<HTMLInputElement>(null);
    const fileInputBannerRef = useRef<HTMLInputElement>(null);
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [userStatistics, setUserStatistics] = useState<UserStatistics | null>(null);
    const [favoriteAnime, setFavoriteAnime] = useState<UserAnimeStatus[]>([]);
    const [animeStats, setAnimeStats] = useState({});
    const [connections, setConnections] = useState<ConnectionsState>({
        friends: [],
        followers: [],
        following: []
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const hasFetchedRef = useRef(false);
    const [activities, setActivities] = useState<UserActivityDTO[]>([]);
    const { makeClickable } = useClickable();

    // Вычисляем isOwnProfile
    const isOwnProfile = location.pathname === '/profile' ||
        (currentUser && nickname && currentUser.nickname === nickname);

    // Новые состояния для редактирования
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({aboutMe: ''});
    const [avatarFile, setAvatarFile] = useState<File | null>(null);
    const [bannerFile, setBannerFile] = useState<File | null>(null);
    const [avatarPreview, setAvatarPreview] = useState<string>('');
    const [bannerPreview, setBannerPreview] = useState<string>('');
    const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
    const [isBannerModalOpen, setIsBannerModalOpen] = useState(false);
    const [originalAvatarFile, setOriginalAvatarFile] = useState<File | null>(null);
    const [originalAvatarPreview, setOriginalAvatarPreview] = useState<string>('');
    const [originalBannerFile, setOriginalBannerFile] = useState<File | null>(null);
    const [originalBannerPreview, setOriginalBannerPreview] = useState<string>('');
    const [saving, setSaving] = useState(false);
    const [friendshipStatus, setFriendshipStatus] = useState<FriendshipStatus | null>(null);
    const [isLoadingFriendship, setIsLoadingFriendship] = useState(false);

    useEffect(() => {
        setUserProfile(null);
        setUserStatistics(null);
        setFavoriteAnime([]);
        setAnimeStats({});
        setActivities([]);
        setConnections({
            friends: [] as Friend[],
            followers: [] as Friend[],
            following: [] as Subscription[]
        });
        setLoading(true);
        setError(null);
        setIsEditing(false);
        setEditData({aboutMe: ''});
        setAvatarFile(null);
        setBannerFile(null);
        setAvatarPreview('');
        setBannerPreview('');
        setIsAvatarModalOpen(false);
        setIsBannerModalOpen(false);
        setOriginalAvatarFile(null);
        setOriginalAvatarPreview('');
        setOriginalBannerFile(null);
        setOriginalBannerPreview('');
        setSaving(false);

        // Сбрасываем флаг загрузки
        hasFetchedRef.current = false;
    }, [nickname, location.pathname, currentUser, isOwnProfile]);

    // Функция для получения статуса дружбы
    const fetchFriendshipStatus = async () => {
        if (!isAuthenticated || isOwnProfile || !nickname) return;

        try {
            const response = await api.get(`/user/friends/status/${nickname}`);
            setFriendshipStatus(response.data);
        } catch (error) {
            console.error('Error fetching friendship status:', error);
            setFriendshipStatus(null);
        }
    };

    // Функция для отправки запроса в друзья
    const sendFriendRequest = async () => {
        if (!isAuthenticated || isOwnProfile || !nickname) return;

        try {
            setIsLoadingFriendship(true);
            await api.post('/user/friends/request', {
                nickname: nickname
            });
            await fetchFriendshipStatus();
        } catch (error: any) {
            alert(error.response?.data || 'Не удалось отправить запрос в друзья');
        } finally {
            setIsLoadingFriendship(false);
        }
    };

    // Функция для обработки действия с друзьями
    const handleFriendAction = async (action: string, friendshipId?: number, keepAsFollower?: boolean) => {
        if (!isAuthenticated || isOwnProfile) return;

        try {
            setIsLoadingFriendship(true);

            switch (action) {
                case 'ADD':
                    await sendFriendRequest();
                    break;
                case 'ACCEPT':
                    if (friendshipId) {
                        await api.post(`/user/friends/${friendshipId}/accept`);
                    }
                    break;
                case 'REJECT':
                    if (friendshipId) {
                        await api.post(`/user/friends/${friendshipId}/reject`);
                    }
                    break;
                case 'CANCEL': // Отмена своего запроса
                    if (friendshipId) {
                        await api.delete(`/user/friends/${friendshipId}/remove`, {
                            params: { keepAsFollower: false }
                        });
                    }
                    break;
                case 'REMOVE':
                    if (friendshipId) {
                        // Если keepAsFollower уже передан (из модального окна)
                        if (keepAsFollower !== undefined) {
                            await api.delete(`/user/friends/${friendshipId}/remove`, {
                                params: { keepAsFollower }
                            });
                        } else {
                            // Показываем диалоговое окно с выбором
                            const userChoice = window.confirm(
                                'Выберите действие:\n\n' +
                                'Нажмите "ОК" - удалить полностью (оба перестанут быть друзьями)\n' +
                                'Нажмите "Отмена" - оставить в подписчиках (человек останется подписанным на вас)'
                            );

                            // true = OK = удалить полностью (keepAsFollower = false)
                            // false = Cancel = оставить в подписчиках (keepAsFollower = true)
                            const keepAsFollowerChoice = !userChoice;

                            await api.delete(`/user/friends/${friendshipId}/remove`, {
                                params: { keepAsFollower: keepAsFollowerChoice }
                            });

                            // Показываем сообщение в зависимости от выбора
                            if (keepAsFollowerChoice) {
                                alert('Пользователь остался в ваших подписчиках');
                            } else {
                                alert('Пользователь полностью удален из друзей');
                            }
                        }
                    }
                    break;
                case 'RESEND': // Повторная отправка после отклонения
                    await sendFriendRequest();
                    break;
            }

            await fetchFriendshipStatus();

            // Обновляем данные о связях после изменения статуса дружбы
            if (isAuthenticated && isOwnProfile) {
                await loadConnectionsData();
            }
        } catch (error: any) {
            alert(error.response?.data || 'Произошла ошибка');
        } finally {
            setIsLoadingFriendship(false);
        }
    };

    const handleStatusClick = (status: string) => {
        if (isOwnProfile) {
            navigate(`/my-list?status=${status}`);
        } else if (nickname) {
            navigate(`/users/${nickname}/list?status=${status}`);
        }
    };

    // Проверка есть ли изменения
    const hasChanges = () => {
        const hasTextChanges = editData.aboutMe !== userProfile?.aboutMe;
        const hasAvatarChanges = !!avatarFile;
        const hasBannerChanges = !!bannerFile;

        return hasTextChanges || hasAvatarChanges || hasBannerChanges;
    };

    const handleEditDataChange = (field: string, value: string) => {
        setEditData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleEditClick = () => {
        if (isEditing) {
            handleSaveChanges();
        } else {
            setIsEditing(true);
            setEditData({
                aboutMe: userProfile?.aboutMe || ''
            });
            setOriginalAvatarPreview(userProfile?.avatarUrl || '');
            setOriginalBannerPreview(userProfile?.bannerUrl || '');
        }
    };

    const handleSaveChanges = async () => {
        // Проверяем есть ли изменения
        if (!hasChanges()) {
            setIsEditing(false);
            setAvatarPreview(''); // Очищаем превью
            setBannerPreview(''); // Очищаем превью
            return;
        }

        try {
            setSaving(true);
            let changesWereMade = false;

            // 1. Обновление описания
            if (editData.aboutMe !== userProfile?.aboutMe) {
                await api.put('/user/profile', {
                    nickname: userProfile?.nickname,
                    aboutMe: editData.aboutMe
                });
                changesWereMade = true;
            }

            // 2. Загрузка аватара
            if (avatarFile) {
                const formData = new FormData();
                formData.append('file', avatarFile);
                await api.post('/user/profile/avatar', formData, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                changesWereMade = true;
            }

            // 3. Загрузка баннера
            if (bannerFile) {
                const formData = new FormData();
                formData.append('file', bannerFile);
                await api.post('/user/profile/banner', formData, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                changesWereMade = true;
            }

            if (changesWereMade) {
                const response = await api.get('/user/profile');
                setUserProfile(response.data);
                alert('Изменения успешно сохранены!');
            }

            setIsEditing(false);
            setAvatarFile(null);
            setBannerFile(null);
            setAvatarPreview('');
            setBannerPreview('');

        } catch (error: any) {
            alert(error.response?.data?.error || 'Произошла ошибка при сохранении изменений');
        } finally {
            setSaving(false);
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setAvatarFile(null);
        setBannerFile(null);
        setAvatarPreview('');
        setBannerPreview('');
    };

    // Обработчики для файлов
    const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Проверка размера файла (2MB)
            if (file.size > 2 * 1024 * 1024) {
                alert('Размер файла не должен превышать 2MB');
                return;
            }

            // Проверка типа файла
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                alert('Разрешены только файлы JPEG, JPG, PNG и WebP');
                return;
            }

            // Сохраняем исходные значения
            setOriginalAvatarFile(avatarFile);
            setOriginalAvatarPreview(avatarPreview);

            setAvatarFile(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                setAvatarPreview(e.target?.result as string);
                setIsAvatarModalOpen(true);
            };
            reader.readAsDataURL(file);
        }
        event.target.value = '';
    };

    const handleBannerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                alert('Размер файла не должен превышать 2MB');
                return;
            }

            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                alert('Разрешены только файлы JPEG, JPG, PNG и WebP');
                return;
            }

            // Сохраняем исходные значения
            setOriginalBannerFile(bannerFile);
            setOriginalBannerPreview(bannerPreview);

            setBannerFile(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                setBannerPreview(e.target?.result as string);
                setIsBannerModalOpen(true);
            };
            reader.readAsDataURL(file);
        }
        event.target.value = '';
    };

    // Обработчики для модальных окон обрезки
    const handleAvatarCropSave = (croppedImage: string) => {

        setAvatarPreview(croppedImage);

        // Конвертируем dataURL в File синхронно
        const dataURLToFile = (dataURL: string, filename: string): File => {
            const arr = dataURL.split(',');
            const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);

            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }

            return new File([u8arr], filename, {type: mime});
        };

        const file = dataURLToFile(croppedImage, 'avatar.jpg');
        setAvatarFile(file);

        setIsAvatarModalOpen(false);
    };

    const handleBannerCropSave = (croppedImage: string) => {

        setBannerPreview(croppedImage);

        // Конвертируем dataURL в File синхронно
        const dataURLToFile = (dataURL: string, filename: string): File => {
            const arr = dataURL.split(',');
            const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);

            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }

            return new File([u8arr], filename, {type: mime});
        };

        const file = dataURLToFile(croppedImage, 'banner.jpg');
        setBannerFile(file);

        setIsBannerModalOpen(false);
    };

    // Обработчик отмены для аватара
    const handleAvatarCropCancel = () => {

        // Восстанавливаем исходные значения
        setAvatarFile(originalAvatarFile);
        setAvatarPreview(originalAvatarPreview);

        // Очищаем исходные значения
        setOriginalAvatarFile(null);
        setOriginalAvatarPreview('');

        if (fileInputAvatarRef.current) {
            fileInputAvatarRef.current.value = '';
        }
        setIsAvatarModalOpen(false);
    };

    const handleBannerCropCancel = () => {

        // Восстанавливаем исходные значения
        setBannerFile(originalBannerFile);
        setBannerPreview(originalBannerPreview);

        // Очищаем исходные значения
        setOriginalBannerFile(null);
        setOriginalBannerPreview('');

        if (fileInputBannerRef.current) {
            fileInputBannerRef.current.value = '';
        }
        setIsBannerModalOpen(false);
    };

    const handleAvatarDelete = async () => {
        if (!window.confirm('Вы уверены, что хотите удалить аватар?')) {
            return;
        }

        try {
            await api.delete('/user/profile/avatar');

            // Обновляем данные профиля
            const response = await api.get('/user/profile');
            setUserProfile(response.data);

            // Очищаем превью и файл
            setAvatarPreview('');
            setAvatarFile(null);

            alert('Аватар успешно удален!');
        } catch (error: any) {
            alert(error.response?.data?.error || 'Произошла ошибка при удалении аватара');
        }
    };

    const handleBannerDelete = async () => {
        if (!window.confirm('Вы уверены, что хотите удалить баннер?')) {
            return;
        }

        try {
            await api.delete('/user/profile/banner');

            // Обновляем данные профиля
            const response = await api.get('/user/profile');
            setUserProfile(response.data);

            // Очищаем превью и файл
            setBannerPreview('');
            setBannerFile(null);

            alert('Баннер успешно удален!');
        } catch (error: any) {
            alert(error.response?.data?.error || 'Произошла ошибка при удалении баннера');
        }
    };

    const loadConnectionsData = async () => {
        try {
            if (isAuthenticated && isOwnProfile) {
                // Для своего профиля получаем реальные данные
                const friendsResponse = await api.get('/user/friends');
                const followersResponse = await api.get('/user/friends/followers');
                const followingResponse = await api.get('/user/friends/following');

                // Приводим данные к правильному формату
                const formattedFriends: Friend[] = (friendsResponse.data || []).map((friend: any) => ({
                    id: friend.user?.id?.toString() || friend.id?.toString() || Math.random().toString(),
                    name: friend.user?.nickname || friend.nickname || 'Неизвестно',
                    avatar: friend.user?.avatarUrl || friend.avatarUrl || '/user-images/default/avatar.jpg'
                }));

                const formattedFollowers: Friend[] = (followersResponse.data || []).map((follower: any) => ({
                    id: follower.id?.toString() || Math.random().toString(),
                    name: follower.nickname || 'Неизвестно',
                    avatar: follower.avatarUrl || '/user-images/default/avatar.jpg'
                }));

                const formattedFollowing: Subscription[] = (followingResponse.data || []).map((followingItem: any) => ({
                    id: followingItem.id?.toString() || Math.random().toString(),
                    name: followingItem.nickname || 'Неизвестно',
                    avatar: followingItem.avatarUrl || '/user-images/default/avatar.jpg',
                    subscribers: 0
                }));

                setConnections({
                    friends: formattedFriends,
                    followers: formattedFollowers,
                    following: formattedFollowing
                });
            } else if (nickname && !isOwnProfile) {
                // Для чужого профиля используем публичные endpoint'ы
                try {
                    // Получаем друзей
                    const friendsResponse = await api.get(`/users/${nickname}/friends`);
                    console.log('Friends API response:', friendsResponse.data);

                    // Получаем подписчиков
                    const followersResponse = await api.get(`/users/${nickname}/followers`);
                    console.log('Followers API response:', followersResponse.data);

                    // Получаем подписки
                    const followingResponse = await api.get(`/users/${nickname}/following`);
                    console.log('Following API response:', followingResponse.data);

                    // Приводим данные к правильному формату
                    const formattedFriends: Friend[] = Array.isArray(friendsResponse.data)
                        ? friendsResponse.data.map((friend: any) => ({
                            id: friend.id?.toString() || Math.random().toString(),
                            name: friend.nickname || friend.username || 'Неизвестно',
                            avatar: friend.avatarUrl || '/user-images/default/avatar.jpg'
                        }))
                        : [];

                    const formattedFollowers: Friend[] = Array.isArray(followersResponse.data)
                        ? followersResponse.data.map((follower: any) => ({
                            id: follower.id?.toString() || Math.random().toString(),
                            name: follower.nickname || 'Неизвестно',
                            avatar: follower.avatarUrl || '/user-images/default/avatar.jpg'
                        }))
                        : [];

                    const formattedFollowing: Subscription[] = Array.isArray(followingResponse.data)
                        ? followingResponse.data.map((followingItem: any) => ({
                            id: followingItem.id?.toString() || Math.random().toString(),
                            name: followingItem.nickname || 'Неизвестно',
                            avatar: followingItem.avatarUrl || '/user-images/default/avatar.jpg',
                            subscribers: 0
                        }))
                        : [];

                    setConnections({
                        friends: formattedFriends,
                        followers: formattedFollowers,
                        following: formattedFollowing
                    });
                } catch (apiError: any) {
                    console.warn('Public API endpoints not available:', apiError);
                    // Если публичные endpoint'ы не работают, показываем заглушку
                    setConnections({
                        friends: [],
                        followers: [],
                        following: []
                    });
                }
            } else {
                // Для неавторизованных пользователей
                setConnections({
                    friends: [],
                    followers: [],
                    following: []
                });
            }
        } catch (error) {
            console.error('Error loading connections data:', error);
            setConnections({
                friends: [],
                followers: [],
                following: []
            });
        }
    };

    useEffect(() => {
        if (isAuthenticated && !isOwnProfile && nickname) {
            fetchFriendshipStatus();
        }
    }, [isAuthenticated, isOwnProfile, nickname]);

    const profileHeaderProps = useMemo(() => {
        if (!userProfile) {
            return null;
        }

        const totalFriends = connections.friends.length;
        const totalFollowers = connections.followers.length;
        const totalFollowing = connections.following.length;

        return {
            username: userProfile.nickname,
            bio: isEditing ? editData.aboutMe : userProfile.aboutMe,
            friends: totalFriends,
            followers: totalFollowers,
            subscriptions: totalFollowing,
            isOwnProfile,
            isEditing,
            editData,
            hasChanges: hasChanges(),
            currentAvatarUrl: userProfile.avatarUrl,
            currentBannerUrl: userProfile.bannerUrl,
            avatarPreview,
            bannerPreview,
            fileInputAvatarRef,
            fileInputBannerRef,
            onEditDataChange: handleEditDataChange,
            onAvatarChange: handleAvatarChange,
            onBannerChange: handleBannerChange,
            onAvatarDelete: handleAvatarDelete,
            onBannerDelete: handleBannerDelete,
            onEditClick: handleEditClick,
            onSaveClick: handleSaveChanges,
            onCancelClick: handleCancelEdit,
            friendshipStatus,
            onFriendAction: handleFriendAction,
            isAuthenticated,
            isLoadingFriendship,
            currentUser,
            userProfile
        };
    }, [
        userProfile,
        connections,
        isEditing,
        editData,
        isOwnProfile,
        hasChanges,
        avatarPreview,
        bannerPreview,
        fileInputAvatarRef,
        fileInputBannerRef,
        handleCancelEdit,
        friendshipStatus,
        isAuthenticated,
        handleFriendAction,
        isLoadingFriendship,
        currentUser,
        userProfile
    ]);

    // Обновленный useEffect для загрузки данных
    useEffect(() => {
        if (hasFetchedRef.current) {
            return;
        }

        const fetchProfileData = async () => {
            try {
                setLoading(true);
                hasFetchedRef.current = true;

                let userResponse: { data: UserProfile };
                let statsResponse: { data: UserStatistics };
                let favoritesResponse: { data: UserAnimeStatus[] };
                let activitiesResponse: { data: UserActivityDTO[] };

                if (isOwnProfile) {
                    // Для своего профиля используем приватные endpoint'ы
                    userResponse = await api.get<UserProfile>('/user/profile');
                    statsResponse = await api.get<UserStatistics>('/user/statistics');
                    favoritesResponse = await api.get<UserAnimeStatus[]>('/user/favorites');
                    activitiesResponse = await api.get<UserActivityDTO[]>('/user/activity/recent');

                    // Загружаем весь список и фильтруем локально
                    const allAnimeResponse = await api.get<UserAnimeStatus[]>('/user/list');
                    const allAnime = allAnimeResponse.data;

                    // Считаем статистику локально
                    const watching = allAnime.filter(anime => anime.status === 'WATCHING').length;
                    const completed = allAnime.filter(anime => anime.status === 'COMPLETED').length;
                    const planned = allAnime.filter(anime => anime.status === 'PLANNED').length;
                    const dropped = allAnime.filter(anime => anime.status === 'DROPPED').length;
                    const rewatching = allAnime.filter(anime => anime.status === 'REWATCHING').length;
                    const favorite = favoritesResponse.data.length;

                    const totalUnique = allAnime.length;

                    setAnimeStats({
                        watching,
                        completed,
                        planned,
                        dropped,
                        rewatching,
                        favorite,
                        total: totalUnique
                    });

                } else if (nickname) {
                    // Для чужого профиля используем публичные endpoint'ы
                    userResponse = await api.get<UserProfile>(`/users/${nickname}`);
                    statsResponse = await api.get<UserStatistics>(`/users/${nickname}/statistics`);
                    favoritesResponse = await api.get<UserAnimeStatus[]>(`/users/${nickname}/favorites`);
                    try {
                        activitiesResponse = await api.get<UserActivityDTO[]>(`/users/${nickname}/activity/recent`);
                    } catch (error) {
                        console.log('Activities endpoint not available, using empty array');
                        activitiesResponse = { data: [] };
                    }

                    // Загружаем весь список и фильтруем локально
                    const allAnimeResponse = await api.get<UserAnimeStatus[]>(`/users/${nickname}/list`);
                    const allAnime = allAnimeResponse.data;

                    // Считаем статистику локально
                    const watching = allAnime.filter(anime => anime.status === 'WATCHING').length;
                    const completed = allAnime.filter(anime => anime.status === 'COMPLETED').length;
                    const planned = allAnime.filter(anime => anime.status === 'PLANNED').length;
                    const dropped = allAnime.filter(anime => anime.status === 'DROPPED').length;
                    const rewatching = allAnime.filter(anime => anime.status === 'REWATCHING').length;
                    const favorite = favoritesResponse.data.length;

                    // ДОБАВЬТЕ ЭТУ СТРОКУ: считаем общее количество аниме
                    const totalUnique = allAnime.length;

                    setAnimeStats({
                        watching,
                        completed,
                        planned,
                        dropped,
                        rewatching,
                        favorite,
                        total: totalUnique
                    });
                } else {
                    throw new Error('Никнейм не указан');
                }

                await loadConnectionsData();

                setUserProfile(userResponse.data);
                setUserStatistics(statsResponse.data);
                setFavoriteAnime(favoritesResponse.data);
                setActivities(activitiesResponse.data);
                setError(null);

                setLoading(false);

            } catch (err: any) {
                console.error('Error fetching profile data:', err);
                setError(err.message || 'Пользователь не найден');
                setLoading(false);
                hasFetchedRef.current = false;
            }
        };

        if (location.pathname === '/profile' || nickname) {
            fetchProfileData();
        } else {
            setError('Никнейм не указан');
            setLoading(false);
        }
    }, [nickname, location.pathname, currentUser, isOwnProfile]);

    useEffect(() => {
        // Обновляем данные о связях при изменении статуса дружбы
        if (isAuthenticated && isOwnProfile) {
            loadConnectionsData();
        }
    }, [friendshipStatus, isAuthenticated, isOwnProfile]);

    // Адаптеры для преобразования данных в формат компонентов

    const adaptToFavoriteAnimeList = () => {
        if (favoriteAnime && favoriteAnime.length > 0) {
            return favoriteAnime.map(anime => ({
                id: anime.id.toString(),
                animeId: anime.animeId,
                title: anime.animeTitle,
                image: anime.animeImageUrl,
                rating: anime.averageRating || anime.rating || 0,
                year: anime.releaseYear ? anime.releaseYear.toString() : 'Неизвестно',
                genre: anime.genres || []
            }));
        }
        return [];
    };

    if (loading) {
        return (
            <div className="profile-loading-state">
                <div className="loading-spinner"></div>
                <p>Загрузка профиля...</p>
            </div>
        );
    }

    if (error && !userProfile) {
        return (
            <div className="profile-error-state">
                <div className="error-emoji">😕</div>
                <h2>Пользователь не найден</h2>
                <p>Попробуйте проверить никнейм или вернуться на главную страницу</p>
                <button
                    className="error-home-btn"
                    onClick={() => navigate('/')}
                >
                    На главную
                </button>
            </div>
        );
    }

    if (!userProfile || !userStatistics) {
        return null;
    }

    const favoriteAnimeListProps = adaptToFavoriteAnimeList();

    if (!profileHeaderProps) {
        return null;
    }

    return (
        <div className="user-profile-container">
            <div className="profile-page-content">
                <ProfileHeader {...profileHeaderProps} />

                {/* Модальные окна для обрезки изображений */}
                <ImageCropModal
                    isOpen={isAvatarModalOpen}
                    onClose={handleAvatarCropCancel}
                    imageSrc={avatarPreview}
                    onSave={handleAvatarCropSave}
                    aspectRatio={1}
                    type="avatar"
                />

                <ImageCropModal
                    isOpen={isBannerModalOpen}
                    onClose={handleBannerCropCancel}
                    imageSrc={bannerPreview}
                    onSave={handleBannerCropSave}
                    aspectRatio={4}
                    type="banner"
                />

                {/* Индикатор сохранения */}
                {saving && (
                    <div className="saving-overlay">
                        <div className="saving-spinner"></div>
                        <p>Сохранение изменений...</p>
                    </div>
                )}

                {/* Статистика аниме */}
                <div className="anime-stats-container">
                    <AnimeStats
                        stats={animeStats}
                        onStatusClick={handleStatusClick}
                        isOwnProfile={isOwnProfile}
                        isAuthenticated={isAuthenticated}
                        nickname={nickname}
                    />
                </div>

                {/* Диаграммы */}
                <div className="charts-container">
                    <DistributionChart stats={animeStats}/>
                    <ProgressChart stats={animeStats}/>
                </div>

                <WatchingActivityChart
                    isOwnProfile={isOwnProfile}
                    nickname={nickname}
                />

                <FavoriteAnimeList
                    animeList={favoriteAnimeListProps}
                    isOwnProfile={isOwnProfile}
                    nickname={nickname}
                />

                <div className="profile-main-layout">
                    {/* Левая колонка - активность */}
                    <div className="activity-column">
                        <ActivityFeed
                            activities={activities}
                            isOwnProfile={isOwnProfile}
                            nickname={nickname}
                        />
                    </div>

                    {/* Правая колонка - друзья и подписки */}
                    <div className="profile-sidebar">
                        <ConnectionsBlock
                            friends={connections.friends}
                            followers={connections.followers}
                            subscriptions={connections.following}
                            currentUserNickname={userProfile?.nickname}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserProfilePage;